import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  ArrowLeft, Save, Trash2, GripVertical, Plus, Camera, Video, 
  X, Loader2, Clock, Users, Edit2, Check, Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, Reorder } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const CATEGORIES = [
  { id: "entree", label: "Entrée", emoji: "🥗" },
  { id: "plat", label: "Plat", emoji: "🍽️" },
  { id: "dessert", label: "Dessert", emoji: "🍰" },
  { id: "boisson", label: "Boisson", emoji: "🥤" },
  { id: "autre", label: "Autre", emoji: "🍳" },
];

const TAGS = [
  { id: "vege", label: "Végétarien", emoji: "🥬" },
  { id: "vegan", label: "Vegan", emoji: "🌱" },
  { id: "sans_gluten", label: "Sans gluten", emoji: "🌾" },
  { id: "sans_cuisson", label: "Sans cuisson", emoji: "❄️" },
  { id: "rapide", label: "Rapide", emoji: "⚡" },
  { id: "facile", label: "Facile", emoji: "👶" },
];

const EditRecipePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [steps, setSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Recipe metadata form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [servings, setServings] = useState(4);
  const [category, setCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!token) {
        navigate("/login");
        return;
      }
      try {
        const response = await axios.get(`${API}/recipes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        
        // Check if user owns this recipe
        if (data.chef_id !== user?.id) {
          toast.error("Vous n'avez pas accès à cette recette");
          navigate("/my-recipes");
          return;
        }

        setRecipe(data);
        setSteps(data.steps || []);
        setName(data.name || "");
        setDescription(data.description || "");
        setServings(data.servings || 4);
        setCategory(data.category || "");
        setSelectedTags(data.tags || []);
      } catch (error) {
        console.error("Error fetching recipe:", error);
        toast.error("Recette introuvable");
        navigate("/my-recipes");
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id, token, navigate, user?.id]);

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
    setHasChanges(true);
  };

  const handleSaveMetadata = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/recipes/${id}/finish`,
        {
          name,
          servings,
          description,
          final_image: recipe.final_image,
          category,
          tags: selectedTags
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Recette mise à jour !");
      setHasChanges(false);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleReorderSteps = async (newOrder) => {
    setSteps(newOrder);
    setHasChanges(true);
    
    try {
      await axios.put(
        `${API}/recipes/${id}/steps/reorder`,
        { step_ids: newOrder.map(s => s.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error("Erreur lors de la réorganisation");
    }
  };

  const handleUpdateStep = async (stepId, updates) => {
    try {
      await axios.put(
        `${API}/recipes/${id}/steps/${stepId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSteps(prev => prev.map(s => 
        s.id === stepId ? { ...s, ...updates, description: updates.original_text || s.description } : s
      ));
      setEditingStep(null);
      toast.success("Étape mise à jour !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteStep = async () => {
    if (!stepToDelete) return;
    
    try {
      await axios.delete(
        `${API}/recipes/${id}/steps/${stepToDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSteps(prev => prev.filter(s => s.id !== stepToDelete));
      setShowDeleteDialog(false);
      setStepToDelete(null);
      toast.success("Étape supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleAddMedia = async (stepId, file, type = "image") => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      
      try {
        if (type === "video") {
          await axios.put(
            `${API}/recipes/${id}/steps/${stepId}`,
            { video_url: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSteps(prev => prev.map(s => 
            s.id === stepId ? { ...s, video_url: base64 } : s
          ));
        } else {
          await axios.put(
            `${API}/recipes/${id}/steps/${stepId}`,
            { media_url: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSteps(prev => prev.map(s => 
            s.id === stepId ? { ...s, media_urls: [...(s.media_urls || []), base64] } : s
          ));
        }
        toast.success(`${type === "video" ? "Vidéo" : "Photo"} ajoutée !`);
      } catch (error) {
        toast.error("Erreur lors de l'ajout du média");
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!recipe) {
    return null;
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to={`/recipe/${id}`}
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la recette
          </Link>

          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal flex items-center gap-2">
              <Edit2 className="w-7 h-7 text-primary" />
              Modifier la recette
            </h1>
            <Button
              onClick={handleSaveMetadata}
              disabled={saving}
              className="rounded-full bg-primary gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Enregistrer
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-4xl mx-auto space-y-6">
        {/* Recipe Metadata */}
        <Card className="shadow-lg">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading font-semibold text-lg text-charcoal mb-4">
              Informations générales
            </h2>

            <div className="space-y-2">
              <Label>Nom de la recette</Label>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); setHasChanges(true); }}
                placeholder="Ex: Tarte aux pommes maison"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => { setDescription(e.target.value); setHasChanges(true); }}
                placeholder="Décrivez votre recette..."
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Portions</Label>
                <Input
                  type="number"
                  value={servings}
                  onChange={(e) => { setServings(parseInt(e.target.value) || 1); setHasChanges(true); }}
                  min={1}
                  max={50}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setHasChanges(true); }}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.emoji} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2">
                {TAGS.map(tag => (
                  <Badge
                    key={tag.id}
                    variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                    className={`cursor-pointer ${
                      selectedTags.includes(tag.id) ? 'bg-primary' : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleTag(tag.id)}
                  >
                    {tag.emoji} {tag.label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Steps Editor */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-lg text-charcoal mb-4 flex items-center gap-2">
              <GustoIcon className="w-5 h-5" />
              Étapes ({steps.length})
            </h2>

            {steps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <GustoLogo size="lg" />
                <p>Aucune étape</p>
              </div>
            ) : (
              <Reorder.Group axis="y" values={steps} onReorder={handleReorderSteps} className="space-y-3">
                {steps.map((step, index) => (
                  <Reorder.Item key={step.id} value={step}>
                    <Card className="border-2 border-dashed border-muted hover:border-primary/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Drag Handle */}
                          <div className="cursor-grab active:cursor-grabbing pt-1">
                            <GripVertical className="w-5 h-5 text-muted-foreground" />
                          </div>

                          {/* Step Number */}
                          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {index + 1}
                          </div>

                          {/* Step Content */}
                          <div className="flex-1 min-w-0">
                            {editingStep === step.id ? (
                              <EditStepForm
                                step={step}
                                onSave={(updates) => handleUpdateStep(step.id, updates)}
                                onCancel={() => setEditingStep(null)}
                              />
                            ) : (
                              <>
                                <p className="text-sm text-charcoal">{step.description}</p>
                                
                                {/* Ingredients */}
                                {step.ingredient && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <Badge variant="secondary" className="text-xs">
                                      {step.ingredient.quantity} {step.ingredient.unit} {step.ingredient.name}
                                    </Badge>
                                  </div>
                                )}

                                {/* Media Preview */}
                                <div className="flex gap-2 mt-3 flex-wrap">
                                  {step.media_urls?.map((url, i) => (
                                    <div key={i} className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                                      <img src={url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                  ))}
                                  {step.video_url && (
                                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted relative">
                                      <video src={step.video_url} className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                        <Video className="w-6 h-6 text-white" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          {/* Actions */}
                          {editingStep !== step.id && (
                            <div className="flex items-center gap-1">
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleAddMedia(step.id, e.target.files[0], "image");
                                  }
                                }}
                              />
                              <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleAddMedia(step.id, e.target.files[0], "video");
                                  }
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => fileInputRef.current?.click()}
                                title="Ajouter une photo"
                              >
                                <Camera className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => videoInputRef.current?.click()}
                                title="Ajouter une vidéo"
                              >
                                <Video className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setEditingStep(step.id)}
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => { setStepToDelete(step.id); setShowDeleteDialog(true); }}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Step Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer l'étape ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. L'étape sera définitivement supprimée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="rounded-full">
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteStep} className="rounded-full">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Step Edit Form Component
const EditStepForm = ({ step, onSave, onCancel }) => {
  const [text, setText] = useState(step.description || "");

  return (
    <div className="space-y-3">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="rounded-xl resize-none text-sm"
        rows={3}
        autoFocus
      />
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full">
          <X className="w-4 h-4 mr-1" />
          Annuler
        </Button>
        <Button size="sm" onClick={() => onSave({ original_text: text })} className="rounded-full bg-primary">
          <Check className="w-4 h-4 mr-1" />
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

export default EditRecipePage;
